import { computed, watch } from 'vue';
import useSessionStore from '@/Stores/useSessionStore';
import useBandwidthControlStore from '@/Stores/useBandwidthControlStore';

function useBandwidthControl(remoteUserProps, streamHandlers) {
  const sessionStore = useSessionStore();
  const bandwidthStore = useBandwidthControlStore();

  const currentPrefs = bandwidthStore.streamPreferences[sessionStore.sessionId];
  let previousPrefs = currentPrefs ? JSON.parse(JSON.stringify(currentPrefs)) : {};

  // Watch for changes in bandwidth preferences
  watch(
    () => bandwidthStore.streamPreferences[sessionStore.sessionId],
    (newPrefs) => {
      // Missing prefs, skipping
      if (!newPrefs) {
        return;
      }

      // Find which streams changed by comparing with previous state
      Object.keys(newPrefs).forEach((jitsiUserId) => {
        const newValue = newPrefs[jitsiUserId];
        const oldValue = previousPrefs?.[jitsiUserId];

        if (newValue !== oldValue) {
          if (newValue) {
            streamHandlers.enableStream(jitsiUserId);
          } else {
            streamHandlers.disableStream(jitsiUserId);
          }
        }
      });

      // Update our tracked previous state (deep clone to avoid reference issues)
      previousPrefs = JSON.parse(JSON.stringify(newPrefs));
    },
    { deep: true },
  );

  // Get available streams for modal
  const availableStreams = computed(() =>
    Object.keys(remoteUserProps)
      .filter((jitsiUserId) => {
        const props = remoteUserProps[jitsiUserId];
        // Only include clinician streams
        return (
          props.name &&
          (/Clinician|vuzix|Stethoscope/.test(props.name) ||
            /Camera|Otoscope|Monitor|Dermatoscope|Exam Camera/.test(props.userId))
        );
      })
      .map((jitsiUserId) => {
        const props = remoteUserProps[jitsiUserId];
        return {
          jitsiUserId,
          label: getStreamLabel(props),
          icon: getStreamIcon(props),
          estimatedBandwidth: getEstimatedBandwidth(props),
          isActive: props.tracks.length > 0 && !props.bandwidthDisabled,
          switchable: getStreamLabel(props) !== 'Main Camera',
        };
      })
      .sort((a, b) => {
        // Put Main Camera first
        const aMain = a.label === 'Main Camera' ? 0 : 1;
        const bMain = b.label === 'Main Camera' ? 0 : 1;
        return aMain - bMain;
      }),
  );

  const getStreamLabel = (props) => {
    if (props.userId === 'Monitor') return 'Monitor';
    if (props.userId === 'Otoscope') return 'Otoscope';
    if (props.userId === 'External Camera') return 'External Camera';
    if (props.userId === 'Dermatoscope') return 'Dermatoscope';
    if (props.userId === 'Exam Camera') return 'Exam Camera';
    if (props.userId === 'vuzix') return 'Smart Glasses';
    if (props.userId === 'Stethoscope' || props.name === 'STETHOSCOPE') return 'Stethoscope';
    if (/Clinician/.test(props.name)) return 'Main Camera';
    return props.name;
  };

  const getStreamIcon = (props) => {
    if (props.userId === 'Monitor') return 'ViceMonitor';
    if (props.userId === 'Otoscope') return 'ViceOtoscope';
    if (props.userId === 'External Camera') return 'ViceCamera';
    if (props.userId === 'Dermatoscope') return 'ViceDermatoscope';
    if (props.userId === 'Exam Camera') return 'ViceExamCam';
    if (props.userId === 'vuzix') return 'ViceGlasses';
    if (props.userId === 'Stethoscope') return 'ViceStethoscope';
    return 'MainCameraIcon'; // Default for main camera
  };

  const getEstimatedBandwidth = (props) => {
    // Rough estimates based on stream type
    if (props.videoType === 'camera') return '~1.5 Mbps';
    if (props.userId === 'vuzix') return '~2 Mbps';
    if (props.userId === 'Stethoscope' || props.name === 'STETHOSCOPE') return '~0.5 Mbps';
    return '~1 Mbps';
  };

  const totalBandwidth = computed(() => {
    let total = 0;
    availableStreams.value.forEach((stream) => {
      const isEnabled = bandwidthStore.isStreamEnabled(sessionStore.sessionId, stream.jitsiUserId);
      if (isEnabled) {
        // Parse bandwidth string to number
        const match = stream.estimatedBandwidth.match(/[\d.]+/);
        if (match) total += parseFloat(match[0]);
      }
    });
    return total.toFixed(1);
  });

  const disabledCount = computed(
    () =>
      availableStreams.value.filter(
        (stream) => !bandwidthStore.isStreamEnabled(sessionStore.sessionId, stream.jitsiUserId),
      ).length,
  );

  return {
    availableStreams,
    totalBandwidth,
    disabledCount,
  };
}

export default useBandwidthControl;
